// eslint-disable @typescript-eslint/no-explicit-any
import { Fragment } from 'react';
import { CheckCircleIcon, MinusIcon } from '@heroicons/react/solid';
import { features } from 'data/static';
import Image from 'next/legacy/image';
import DrawerButton from '../Button/DrawerButton';

import Image1 from '@/images/all-plans/panel-icon.png';
import Image2 from '@/images/all-plans/checklist-icon.png';
import Image3 from '@/images/all-plans/construction-man-icon.png';
import Image4 from '@/images/all-plans/check-badge-icon.png';
import Image5 from '@/images/all-plans/hand-money-icon.png';
import { StaticImageData } from 'next/image';

const tiers = [
  {
    name: 'Solar Panels',
    href: '/best-solar-panels-for-homes',
    priceMonthly: 9,
    description: 'Lowers the cost of power for your home',
  },
  {
    name: 'Solar + Partial Home Backup',
    href: '/best-home-battery-systems',
    priceMonthly: 29,
    description: 'Stay powered during grid blackouts',
  },
  {
    name: 'Solar + Whole Home Backup',
    href: '#',
    priceMonthly: 59,
    description: '...',
  },
];
const sections = [
  {
    name: 'Energy Generation',
    features: [
      {
        name: 'High-efficiency solar panels',
        active: [true, true, true],
      },
      {
        name: 'Generate your own power',
        active: [true, true, true],
      },
      {
        name: 'Lower your utility bills',
        active: [true, true, true],
      },
      {
        name: 'Rate hike protection',
        active: [true, true, true],
      },
      {
        name: '25-year equipment warranty',
        active: [true, true, true],
      },
      {
        name: 'Industry-leading inverters',
        active: [true, true, true],
      },
      {
        name: 'Energy monitoring app',
        active: [true, true, true],
      },
    ],
  },
  {
    name: 'Energy Storage',
    features: [
      {
        name: 'Top lithium-ion batteries',
        active: [false, true, true],
      },
      {
        name: 'Smart power management',
        active: [false, true, true],
      },
      { name: 'Store excess solar power', active: [false, true, true] },
      {
        name: 'Utility blackout protection',
        active: [false, true, true],
      },
      {
        name: 'Backup critical loads',
        active: [false, true, true],
      },
      {
        name: '24/7 off-grid capability',
        active: [false, false, true],
      },
      {
        name: 'Maximum savings/protection',
        active: [false, false, true],
      },
    ],
  },
  {
    name: 'Professional Installation',
    features: [
      {
        name: 'Full turnkey service',
        active: [true, true, true],
      },
      {
        name: 'Detailed system design',
        active: [true, true, true],
      },
      {
        name: 'All permits/utility approval',
        active: [true, true, true],
      },
      {
        name: 'Full project management',
        active: [true, true, true],
      },
      {
        name: 'Workmanship warranty',
        active: [true, true, true],
      },
      {
        name: 'EnergyPal added support',
        active: [true, true, true],
      },
    ],
  },
];

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

type PlanItem = {
  name: string;
  image: StaticImageData;
};
const allPlanItems: PlanItem[] = [
  {
    name: 'Customized solar design',
    image: Image1,
  },
  {
    name: 'Free comparative quotes',
    image: Image2,
  },
  {
    name: 'Professional installation',
    image: Image3,
  },
  {
    name: 'Industry-leading warranties',
    image: Image4,
  },
  {
    name: 'Best product & price promise',
    image: Image5,
  },
];

function AllPlansItem({ name, image }: PlanItem) {
  return (
    <div className="flex flex-row px-8 place-items-center py-4">
      <div className="relative w-8 h-8">
        <Image
          src={image}
          alt={name}
          layout="fill"
          objectFit="contain"
          objectPosition="center center"
        />
      </div>
      <div className="flex items-center pl-2 text-sm leading-4 w-[10rem]">
        {name}
      </div>
    </div>
  );
}
function AllPlansInclude() {
  return (
    <div className="mb-8">
      <h5 className="mb-2 text-lg font-bold text-center">All plans include:</h5>
      <div className="scrollbar flex scroll-smooth overflow-x-auto overflow-y-hidden md:overflow-hidden md:grid md:gap-4 md:grid-cols-5 ">
        {allPlanItems.map((item) => (
          <AllPlansItem key={item.name} {...item} />
        ))}
      </div>
    </div>
  );
}

export default function FinancingPricingTable() {
  return (
    <div className="">
      <div className="py-8 mx-auto -mt-0 bg-white md:-mt-16 max-w-7xl sm:py-12 sm:px-6 lg:px-8 rounded-xl">
        <AllPlansInclude />
        {/* xs to lg */}
        <div className="max-w-2xl mx-auto space-y-16 lg:hidden">
          {features.map((feature, tierIdx) => (
            <section key={feature.name}>
              <div className="px-4 mb-8">
                <h2 className="text-xl font-bold leading-6 text-center text-gray-900">
                  {feature.name}
                </h2>
                <Image src={feature.image} alt={feature.name} />
                {/* <p className="mt-4">
                  <span className="text-4xl font-extrabold text-gray-900">
                    ${tier.priceMonthly}
                  </span>{' '}
                  <span className="text-base font-medium text-gray-500">
                    /mo
                  </span>
                </p>
                <p className="mt-4 text-sm text-gray-500">{tier.description}</p>
                <a
                  href={tier.href}
                  className="block w-full py-2 mt-6 text-sm font-semibold text-center text-white bg-gray-800 border border-gray-800 rounded-md hover:bg-gray-900"
                >
                  Buy {tier.name}
                </a> */}
              </div>

              {sections.map((section) => (
                <table key={section.name} className="w-full">
                  <caption className="px-4 py-3 text-lg font-medium text-left text-gray-900 border-t border-gray-200 bg-gray-50">
                    {section.name}
                  </caption>
                  <thead>
                    <tr>
                      <th className="sr-only" scope="col">
                        Feature
                      </th>
                      <th className="sr-only" scope="col">
                        Included
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {section.features.map((feature) => (
                      <tr
                        key={feature.name}
                        className="border-t border-gray-200"
                      >
                        <th
                          className="px-4 py-5 text-lg font-normal text-left text-gray-900"
                          scope="row"
                        >
                          {feature.name}
                        </th>
                        <td className="py-5 pr-4">
                          {typeof feature.active[tierIdx] === 'string' ? (
                            <span className="block text-lg text-right text-gray-700">
                              {feature.active[tierIdx]}
                            </span>
                          ) : (
                            <>
                              {feature.active[tierIdx] === true ? (
                                <CheckCircleIcon
                                  className="w-8 h-8 ml-auto text-green-500"
                                  aria-hidden="true"
                                />
                              ) : (
                                <MinusIcon
                                  className="w-8 h-8 ml-auto text-gray-400"
                                  aria-hidden="true"
                                />
                              )}

                              <span className="sr-only">
                                {feature.active[tierIdx] === true
                                  ? 'Yes'
                                  : 'No'}
                              </span>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ))}

              <div
                className={classNames(
                  tierIdx < tiers.length - 1 ? 'py-5 border-b' : 'pt-5',
                  'border-t border-gray-200 px-4 text-center'
                )}
              >
                <DrawerButton size="xl">Get a Free Quote</DrawerButton>
                {/* <a
                  href={feature.href}
                  className="block w-full py-2 text-sm font-semibold text-center text-white bg-gray-800 border border-gray-800 rounded-md hover:bg-gray-900"
                >
                  Buy {feature.name}
                </a> */}
              </div>
            </section>
          ))}
        </div>

        {/* lg+ */}
        <div className="hidden lg:block">
          <table className="w-full h-px table-fixed">
            <caption className="sr-only">Pricing plan comparison</caption>

            <thead className="sticky top-16 z-10 bg-white">
              <tr>
                <th
                  className="px-6 text-lg font-medium text-left text-gray-900"
                  scope="col"
                >
                  <span className="sr-only">Feature by</span>
                  <span></span>
                </th>

                {features.map((feature) => (
                  <th
                    key={feature.name}
                    className="w-1/4 px-6 pb-2  text-lg font-bold leading-6 text-center text-gray-900 pt-4"
                    scope="col"
                  >
                    <div className="relative table h-full px-6 py-2  align-top">
                      <Image src={feature.image} alt={feature.name} />
                      {/* <p>
                        <span className="text-4xl font-extrabold text-gray-900">
                          ${tier.priceMonthly}
                        </span>{' '}
                        <span className="text-base font-medium text-gray-500">
                          /mo
                        </span>
                      </p>
                      <p className="mt-4 mb-16 text-sm text-gray-500">
                        {tier.description}
                      </p>
                      <a
                        href={tier.href}
                        className="absolute bottom-0 flex-grow block w-full py-2 text-sm font-semibold text-center text-white bg-gray-800 border border-gray-800 rounded-md 5 hover:bg-gray-900"
                      >
                        Buy {tier.name}
                      </a> */}
                    </div>

                    {feature.name}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="border-gray-200 divide-y divide-gray-200">
              {sections.map((section) => (
                <Fragment key={section.name}>
                  <tr>
                    <th
                      className="py-3 pl-6 text-lg font-medium text-left text-gray-900 bg-gray-50"
                      colSpan={4}
                      scope="colgroup"
                    >
                      {section.name}
                    </th>
                  </tr>
                  {section.features.map((feature) => (
                    <tr key={feature.name}>
                      <th
                        className="px-6 py-5 text-lg font-normal text-left text-gray-900"
                        scope="row"
                      >
                        {feature.name}
                      </th>
                      {tiers.map((tier, idx) => (
                        <td key={tier.name} className="px-6 py-5">
                          {typeof feature.active[idx] === 'string' ? (
                            <span className="block text-lg text-gray-700">
                              {feature.active[idx]}
                            </span>
                          ) : (
                            <>
                              {feature.active[idx] === true ? (
                                <CheckCircleIcon
                                  className="w-8 h-8 mx-auto text-green-500"
                                  aria-hidden="true"
                                />
                              ) : (
                                <MinusIcon
                                  className="w-8 h-8 mx-auto text-gray-400"
                                  aria-hidden="true"
                                />
                              )}

                              <span className="sr-only">
                                {feature.active[idx] === true
                                  ? 'Included'
                                  : 'Not included'}{' '}
                                in {tier.name}
                              </span>
                            </>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200">
                <th className="sr-only" scope="row">
                  Choose your plan
                </th>
                {tiers.map((tier) => (
                  <td key={tier.name} className="px-6 pt-5 text-center">
                    <DrawerButton size="lg">Get a Free Quote</DrawerButton>
                    {/* <a
                      href={tier.href}
                      className="block w-full py-2 text-sm font-semibold text-center text-white bg-gray-800 border border-gray-800 rounded-md hover:bg-gray-900"
                    >
                      Buy {tier.name}
                    </a> */}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
